import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { User } from './userService';

// 用户相关接口
export interface FirestoreUser {
  id: string;
  username: string;
  email: string;
  avatar: string;
  provider: string;
  emailVerified: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastLoginAt: Timestamp;
  credits: number;
}

// 壁纸相关接口
export interface FirestoreWallpaper {
  id: string;
  userId: string;
  title: string;
  style: 'generated' | 'online';
  thumbnail: string;
  fullImageUrl: string;
  isWatermarked: boolean;
  downloads: number;
  prompt?: string;
  dimensions: {
    width: number;
    height: number;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// 历史记录接口
export interface FirestoreHistoryItem {
  id: string;
  userId: string;
  title: string;
  style: 'generated' | 'online';
  createdAt: string;
  thumbnail: string;
  fullImageUrl: string;
  isWatermarked: boolean;
  downloads: number;
  prompt?: string;
  dimensions: {
    width: number;
    height: number;
  };
}

class FirestoreService {
  // ==================== 用户相关操作 ====================
  
  // 创建或更新用户
  async createOrUpdateUser(user: User): Promise<void> {
    try {
      const userRef = doc(db, 'users', user.id);
      const userData: FirestoreUser = {
        ...user,
        createdAt: user.createdAt ? Timestamp.fromDate(new Date(user.createdAt)) : serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        lastLoginAt: user.lastLoginAt ? Timestamp.fromDate(new Date(user.lastLoginAt)) : serverTimestamp() as Timestamp
      };
      
      await setDoc(userRef, userData, { merge: true });
      console.log('用户数据已保存到 Firestore:', user.id);
    } catch (error) {
      console.error('保存用户数据失败:', error);
      throw error;
    }
  }

  // 获取用户信息
  async getUser(userId: string): Promise<FirestoreUser | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data() as FirestoreUser;
      }
      return null;
    } catch (error) {
      console.error('获取用户数据失败:', error);
      throw error;
    }
  }

  // 更新用户信息
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updateData);
      console.log('用户信息已更新:', userId);
    } catch (error) {
      console.error('更新用户信息失败:', error);
      throw error;
    }
  }

  // 删除用户
  async deleteUser(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      console.log('用户已删除:', userId);
    } catch (error) {
      console.error('删除用户失败:', error);
      throw error;
    }
  }

  // ==================== 壁纸相关操作 ====================
  
  // 保存壁纸到历史记录
  async saveWallpaper(wallpaper: Omit<FirestoreWallpaper, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('🔥 Firestore: 准备保存壁纸数据:', wallpaper);
      
      const wallpapersRef = collection(db, 'wallpapers');
      const wallpaperData = {
        ...wallpaper,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('🔥 Firestore: 最终保存数据:', wallpaperData);
      
      const docRef = await addDoc(wallpapersRef, wallpaperData);
      console.log('✅ 壁纸已保存到 Firestore:', docRef.id);
      
      // 保存后自动清理超出限制的壁纸
      try {
        await this.cleanupWallpapers(wallpaper.userId, wallpaper.style, 50);
      } catch (cleanupError) {
        console.error('❌ 清理壁纸失败，但不影响保存:', cleanupError);
        // 清理失败不影响主流程
      }
      
      return docRef.id;
    } catch (error) {
      console.error('❌ 保存壁纸失败:', error);
      if (error instanceof Error) {
        console.error('❌ 错误详情:', error.message);
        console.error('❌ 错误堆栈:', error.stack);
      }
      throw error;
    }
  }

  // 获取用户壁纸历史（按类型分别获取）
  async getUserWallpapers(userId: string, style?: 'generated' | 'online', limitCount: number = 50): Promise<FirestoreHistoryItem[]> {
    try {
      console.log('🔥 Firestore: 开始获取用户壁纸，用户ID:', userId, '类型:', style || 'all');
      const wallpapersRef = collection(db, 'wallpapers');
      
      let q;
      if (style) {
        // 按类型查询
        q = query(
          wallpapersRef,
          where('userId', '==', userId),
          where('style', '==', style),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else {
        // 获取所有类型
        q = query(
          wallpapersRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }
      
      console.log('🔥 Firestore: 执行查询...');
      const querySnapshot = await getDocs(q);
      console.log('🔥 Firestore: 查询完成，文档数量:', querySnapshot.size);
      
      const wallpapers: FirestoreHistoryItem[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('🔥 Firestore: 处理文档:', doc.id, data);
        wallpapers.push({
          id: doc.id,
          userId: data.userId,
          title: data.title,
          style: data.style,
          createdAt: data.createdAt?.toDate().toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          thumbnail: data.thumbnail,
          fullImageUrl: data.fullImageUrl,
          isWatermarked: data.isWatermarked,
          downloads: data.downloads || 0,
          prompt: data.prompt,
          dimensions: data.dimensions
        });
      });
      
      console.log('🔥 Firestore: 返回壁纸数量:', wallpapers.length);
      return wallpapers;
    } catch (error) {
      console.error('❌ Firestore: 获取壁纸历史失败:', error);
      if (error instanceof Error) {
        console.error('❌ Firestore: 错误详情:', error.message);
      }
      throw error;
    }
  }

  // 删除壁纸
  async deleteWallpaper(wallpaperId: string): Promise<void> {
    try {
      const wallpaperRef = doc(db, 'wallpapers', wallpaperId);
      await deleteDoc(wallpaperRef);
      console.log('壁纸已删除:', wallpaperId);
    } catch (error) {
      console.error('删除壁纸失败:', error);
      throw error;
    }
  }

  // 清空用户壁纸历史
  async clearUserWallpapers(userId: string): Promise<void> {
    try {
      const wallpapersRef = collection(db, 'wallpapers');
      const q = query(wallpapersRef, where('userId', '==', userId));
      
      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      await Promise.all(deletePromises);
      console.log('用户壁纸历史已清空:', userId);
    } catch (error) {
      console.error('清空壁纸历史失败:', error);
      throw error;
    }
  }

  // 更新壁纸下载次数
  async incrementWallpaperDownloads(wallpaperId: string): Promise<void> {
    try {
      const wallpaperRef = doc(db, 'wallpapers', wallpaperId);
      await updateDoc(wallpaperRef, {
        downloads: serverTimestamp(), // 这里应该用 increment，但 Firestore 的 increment 需要特殊处理
        updatedAt: serverTimestamp()
      });
      console.log('壁纸下载次数已更新:', wallpaperId);
    } catch (error) {
      console.error('更新下载次数失败:', error);
      throw error;
    }
  }

  // 检查壁纸是否已存在（用于在线壁纸去重）
  async checkWallpaperExists(userId: string, imageUrl: string): Promise<boolean> {
    try {
      const wallpapersRef = collection(db, 'wallpapers');
      const q = query(
        wallpapersRef,
        where('userId', '==', userId),
        where('fullImageUrl', '==', imageUrl)
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('检查壁纸存在性失败:', error);
      return false;
    }
  }

  // 获取用户统计信息
  async getUserStats(userId: string): Promise<{
    totalWallpapers: number;
    generatedWallpapers: number;
    onlineWallpapers: number;
    totalDownloads: number;
  }> {
    try {
      const wallpapersRef = collection(db, 'wallpapers');
      const q = query(wallpapersRef, where('userId', '==', userId));
      
      const querySnapshot = await getDocs(q);
      let totalWallpapers = 0;
      let generatedWallpapers = 0;
      let onlineWallpapers = 0;
      let totalDownloads = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalWallpapers++;
        if (data.style === 'generated') {
          generatedWallpapers++;
        } else if (data.style === 'online') {
          onlineWallpapers++;
        }
        totalDownloads += data.downloads || 0;
      });
      
      return {
        totalWallpapers,
        generatedWallpapers,
        onlineWallpapers,
        totalDownloads
      };
    } catch (error) {
      console.error('获取用户统计失败:', error);
      throw error;
    }
  }

  // 获取用户特定类型的壁纸数量
  async getUserWallpaperCount(userId: string, style: 'generated' | 'online'): Promise<number> {
    try {
      const wallpapersRef = collection(db, 'wallpapers');
      const q = query(
        wallpapersRef,
        where('userId', '==', userId),
        where('style', '==', style)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('获取用户壁纸数量失败:', error);
      return 0;
    }
  }

  // 清理超出限制的壁纸（保留最新的50个）
  async cleanupWallpapers(userId: string, style: 'generated' | 'online', maxCount: number = 50): Promise<void> {
    try {
      console.log(`🧹 开始清理用户 ${userId} 的 ${style} 壁纸，限制为 ${maxCount} 个`);
      
      // 获取该类型的所有壁纸，按创建时间排序（最新的在前）
      const wallpapersRef = collection(db, 'wallpapers');
      const q = query(
        wallpapersRef,
        where('userId', '==', userId),
        where('style', '==', style),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const wallpapers = querySnapshot.docs;
      
      console.log(`🧹 找到 ${wallpapers.length} 个 ${style} 壁纸`);
      
      // 如果超过限制，删除最早的（数组后面的）
      if (wallpapers.length > maxCount) {
        const toDelete = wallpapers.slice(maxCount); // 获取需要删除的壁纸
        console.log(`🧹 需要删除 ${toDelete.length} 个 ${style} 壁纸`);
        
        // 批量删除
        const deletePromises = toDelete.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        console.log(`✅ 成功删除 ${toDelete.length} 个 ${style} 壁纸`);
      } else {
        console.log(`✅ ${style} 壁纸数量未超过限制，无需清理`);
      }
    } catch (error) {
      console.error(`清理 ${style} 壁纸失败:`, error);
      throw error;
    }
  }
}

export const firestoreService = new FirestoreService();
export default firestoreService;
